import React from "react";

/**
 * Dox mascot — the ONE official character.
 *
 * BRAND CONSISTENCY (non-negotiable, per Doug 2026-08-06):
 * Every appearance of Dox across the product renders the master mascot
 * asset — served as `/logo-mascot-hd.png` (2236×2004) with a `/logo-mascot-2x.png`
 * 1x fallback via srcSet, matching the character in the EZDoxAI logo pixel-for-pixel.
 * No alternate illustrations. No generic chatbot avatars. No stock AI
 * assistant graphics. No proportion / smile / eye variations.
 *
 * Motion (breathing, nod, whoosh, sparkle) is applied at the WRAPPER
 * level (DoxAmbient / DoxWorkPerformance / DoxPreview). The character
 * itself is a fixed asset and is never swapped for an alternate.
 *
 * Props:
 *   size       pixel size (width; height is auto to preserve aspect)
 *   emotion    kept for API compatibility. Applies a subtle brightness/
 *              glow hint — never a character change.
 *   className  extra classes
 *   alt        accessible label
 */
export function DoxMascot({ className = "", size = 44, emotion = "idle", alt = "Dox — your AI Compliance Employee" }) {
  const filter =
    emotion === "done" || emotion === "celebrating"
      ? "drop-shadow(0 0 12px rgba(30,115,232,0.35))"
      : emotion === "thinking" || emotion === "reading"
      ? "saturate(0.92) brightness(0.98)"
      : "none";
  // Use a high-resolution master (2236×2004) served via srcset so retina and
  // scaled-down displays get Apple-level smooth edges instead of the tiny
  // 559×501 raster upsampling that produced sticker-like jaggies.
  return (
    <img
      src="/logo-mascot-hd.png"
      srcSet="/logo-mascot-2x.png 1x, /logo-mascot-hd.png 2x"
      alt={alt}
      draggable={false}
      data-testid="dox-mascot"
      width={size}
      height={size}
      style={{
        width: size,
        height: "auto",
        filter,
        transition: "filter 0.45s ease",
        userSelect: "none",
        pointerEvents: "none",
        imageRendering: "auto",
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",
        transform: "translateZ(0)",  // hint the compositor toward high-quality scaling
      }}
      className={className}
    />
  );
}

/**
 * EZDoxAI corporate brand lockup — the approved final asset.
 *
 * BRAND RULES (locked): rendered AS-IS, no recolor / recrop / filter.
 * Two approved masters (dark default, light for white surfaces).
 */
export function Logo({ className = "", maxWidth, theme = "dark" }) {
  const style = maxWidth ? { width: `clamp(200px, 36vw, ${maxWidth}px)` } : undefined;
  const isLight = theme === "light";
  const webp = isLight ? "/ezdoxai-brand-logo.webp" : "/ezdoxai-brand-logo-dark.webp";
  const png = isLight ? "/ezdoxai-brand-logo.png" : "/ezdoxai-brand-logo-dark.png";
  return (
    <picture>
      <source srcSet={webp} type="image/webp" />
      <img
        src={png}
        alt="EZDoxAI — Let Dox do it."
        className={`ezdoxai-brand-logo ${className}`}
        style={style}
        draggable={false}
        data-testid="ezdoxai-logo"
      />
    </picture>
  );
}

export function BrandHero({ align = "center", className = "", theme = "dark" }) {
  const justify = align === "left" ? "justify-start" : "justify-center";
  return (
    <div className={`relative w-full flex ${justify} ${className}`} data-testid="brand-hero">
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[520px] w-[1200px] max-w-full rounded-full opacity-40 blur-3xl"
          style={{ background: "radial-gradient(closest-side, rgba(30,115,232,0.35), transparent 70%)" }}
        />
      </div>
      <Logo theme={theme} />
    </div>
  );
}
